(ns app.front.view.search.saved-searches
  (:require [app.domain.property.model :as property-model]
            [app.front.dashboard.search :as dashboard-search]
            [app.front.entities :as entities]
            [app.front.ui.filter-common :as filter-common]
            [app.i18n.model :as i18n-model]
            [app.query.filter-util :as filter-util]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.util.string :as str-util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


;;
;; Pure logic
;;

(def subtype->entity-tab
  {:filter.subtype/workspace               :workspace
   :filter.subtype/property                :property
   :filter.subtype/licence                 :licence
   :filter.subtype/contractor-registration :contractor-registration
   :filter.subtype/complaint               :complaint})

(def supported-subtypes (set (keys subtype->entity-tab)))

(def entity-tab->subtype
  (set/map-invert subtype->entity-tab))

(defn save-enabled?
  "True for a search term or filters different from the tab defaults; Organization scope alone does not count."
  [{:keys [entity-tab search-term filter-values default-filter-values state-filter attribute-filters]}]
  (boolean
    (or (not (str/blank? search-term))
        (if (= :property entity-tab)
          (or (not= (or state-filter property-model/default-search-states) property-model/default-search-states)
              (seq (remove #(str/blank? (:value %)) attribute-filters)))
          (not= filter-values default-filter-values)))))

(defn build-save-request
  "Build :user/save-unified-filter data from the prepared save-data inputs."
  [{:keys [name subtype search-term domain-group-ids filter-values property-search]}]
  (p/assoc-if {:filter/name       (some-> name str/trim)
               :filter/subtype    subtype
               :filter/conditions (if property-search
                                    []
                                    (filter-common/filter-values->conditions filter-values))}
              :filter/search-term     (str-util/blank->nil search-term)
              :filter/domain-groups   (not-empty domain-group-ids)
              :filter/property-search property-search))

(defn preview-text
  "Human-readable breadcrumb of a saved search: municipalities > \"term\" > tab > filter values.
  :filter-value-names is a seq of display-name strings (nil entries ignored). Empty segments
  are omitted."
  [{:keys [domain-group-names search-term tab-label filter-value-names]}]
  (->> [(when (seq domain-group-names)
          (str/join ", " domain-group-names))
        (when-not (str/blank? search-term)
          (str "\"" search-term "\""))
        tab-label
        (let [filter-names (remove nil? filter-value-names)]
          (when (seq filter-names)
            (str/join ", " filter-names)))]
       (remove nil?)
       (str/join " > ")))

(defn- property-filter-value-label
  "Format a filled Property filter value for a preview."
  [{:keys [lang tr format-date templates]} {:keys [template-id type value]}]
  (case type
    (:property-attribute-template.type/select
      :property-attribute-template.type/text)
    (str-util/blank->nil (str value))

    (:property-attribute-template.type/integer
      :property-attribute-template.type/decimal)
    (some-> value str)

    :property-attribute-template.type/toggle
    (when (some? value)
      (let [template (p/find-first templates #(= template-id (:db/id %)))]
        (if value
          (property-model/toggle-attribute-true-label template lang tr)
          (property-model/toggle-attribute-false-label template lang tr))))

    (:property-attribute-template.type/date
      :property-attribute-template.type/date-range)
    (when (inst? value)
      (format-date value))
    ;; A template no longer in the config has no type, so it has no label.
    nil))

(defn- saved-values->set
  "Saved filter values as a set; scalars become one-element sets and tuples stay whole."
  [values]
  (cond
    (set? values)        values
    (sequential? values) (set values)
    values               #{values}
    :else                #{}))

(defn- filter-value-names
  "Display labels for standard-tab filter values, in the tab's condition-attribute order, each
  attribute's labels sorted. Attributes outside the order are omitted and nil labels are dropped."
  [val->str-fn attribute-order filter-values]
  (into []
        (mapcat (fn [attr]
                  (->> (saved-values->set (get filter-values attr))
                       (keep (val->str-fn attr))
                       sort)))
        attribute-order))

(defn- saved-val->str-fn
  "Wraps a tab's configured val->str-fn with the shared saved-description formatting."
  [val->str-fn tr]
  (fn [attr]
    (if (= :workspace/category attr)
      (fn [value]
        (if (vector? value)
          (let [[permit-type category] value]
            (str category " (" (tr [permit-type]) ")"))
          ((val->str-fn attr) value)))
      (val->str-fn attr))))

(defn description-attribute-order
  "Attributes shown in saved-search descriptions, excluding feature-gated ones while disabled."
  [{:keys [filter-condition-attributes] :as search-mode} feature-enabled?]
  (let [hidden-attrs (dashboard-search/hidden-filter-attributes search-mode feature-enabled?)]
    (into [] (remove hidden-attrs) filter-condition-attributes)))

(defn- property-filter-value-name
  "The description segment of one Property attribute filter: \"name: label\", or nil."
  [opts {:keys [name] :as attribute}]
  (when-let [label (property-filter-value-label opts attribute)]
    (str name ": " label)))

(defn preview-filter-names
  "Display labels for the filled filter values of the save dialog preview."
  [{:keys [entity-tab val->str-fn attribute-order filter-values property-search tr] :as opts}]
  (if (= :property entity-tab)
    (->> (:filters property-search)
         (keep #(property-filter-value-name opts %))
         vec)
    (filter-value-names (saved-val->str-fn val->str-fn tr) attribute-order filter-values)))

(defn property-snapshot-filters
  "Snapshot attribute filters in display order, including controls without a value."
  [lang templates attribute-filters]
  (mapv (fn [{:keys [template-id value]}]
          (let [{:property-attribute-template/keys [type options] :as template}
                (p/find-first templates #(= template-id (:db/id %)))]
            (p/assoc-if {:template-id template-id
                         :type        type
                         :name        (or (property-model/attribute-template-name template lang)
                                          (str template-id))}
                        :value     value
                        :option-id (when (and (= type :property-attribute-template.type/select) value)
                                     ;; The live select stores the label in the current UI language.
                                     (-> options
                                         (p/find-first #(= value (property-model/attribute-template-option-name % lang)))
                                         :db/id)))))
        attribute-filters))

(defn property-snapshot
  "Snapshot Property states and ordered filters. An empty state set means all states."
  [states templates lang attribute-filters]
  {:states  (or states property-model/default-search-states)
   :filters (property-snapshot-filters lang templates attribute-filters)})

(defn resolve-restore-scope
  "Split saved group ids into :effective and :lost. :effective nil means keep the current selection."
  [saved-group-ids loaded-group-ids]
  (if (nil? saved-group-ids)
    {:effective nil :lost #{}}
    (let [lost (set/difference saved-group-ids loaded-group-ids)]
      {:effective (set/difference saved-group-ids lost)
       :lost      lost})))

(defn restore-payload
  "Extract saved search inputs. For Organization scope, nil preserves the selection; an empty set means unrestricted."
  [filter-entity]
  (let [domain-group-ids (some-> filter-entity (entities/get-id :filter/domain-groups))]
    (p/assoc-if {:entity-tab       (subtype->entity-tab (:filter/subtype filter-entity))
                 :search-term      (:filter/search-term filter-entity)
                 :domain-group-ids (if (:filter/domain-group-scope-saved? filter-entity)
                                     (or domain-group-ids #{})
                                     domain-group-ids)
                 :filter-values    (filter-util/filter-entity->search-values-map filter-entity)}
                :property-search (:filter/property-search filter-entity))))

(defn resolve-available-values
  "Restore saved filters supported by the current search configuration.
  Keep only available options for ordinary filters and valid ranges for available date-range filters.
  Omit unsupported attributes and invalid ranges. Return :resolved and :dropped?, which indicates
  whether any saved value was omitted."
  [search-config filter-values filter-value-fn]
  (let [available-values (dashboard-search/filter-values-from-options (:filter-values search-config)
                                                                      filter-value-fn)]
    (reduce-kv (fn [{:keys [resolved] :as result} attr values]
                 (let [saved-values (saved-values->set values)
                       available    (get available-values attr #{})
                       kept         (cond
                                      (not (filter-common/date-range-values? available))
                                      (set/intersection saved-values available)

                                      (filter-util/valid-date-range-values? saved-values)
                                      saved-values

                                      :else
                                      #{})]
                   (cond-> (assoc result :resolved (p/assoc-if resolved attr (not-empty kept)))
                     (not= saved-values kept) (assoc :dropped? true))))
               {:resolved {} :dropped? false}
               filter-values)))

(defn- resolve-select-value
  "Resolve the current label by option id, then by a unique translated-label match; otherwise nil."
  [lang options {:keys [value option-id]}]
  (or (when option-id
        (some-> (p/find-first options #(= option-id (:db/id %)))
                (property-model/attribute-template-option-name lang)))
      (when value
        (let [matches (filter #(some #{value}
                                     (->> (:property-attribute-template-option/values %)
                                          i18n-model/strings-map
                                          vals))
                              options)]
          (when (= 1 (count matches))
            (property-model/attribute-template-option-name (first matches) lang))))))

(defn- type-compatible?
  [saved-type template-type]
  (or (= saved-type template-type)
      (contains? (set (get property-model/compatible-template-types saved-type)) template-type)))

(defn- resolve-property-filter
  "Resolve a saved filter against current templates, or return nil. Controls without a value remain valid."
  [lang templates {:keys [template-id type value] :as saved}]
  (let [template      (p/find-first templates #(= template-id (:db/id %)))
        id            (:db/id template)
        template-type (:property-attribute-template/type template)
        options       (:property-attribute-template/options template)]
    (when (and id (type-compatible? type template-type))
      (if (= template-type :property-attribute-template.type/select)
        (let [resolved (resolve-select-value lang options saved)]
          (when (or (nil? value) (some? resolved))
            (p/assoc-if {:template-id id} :value (or resolved value))))
        (when (or (nil? value) (property-model/valid-search-attribute-value? template-type value))
          (p/assoc-if {:template-id id} :value value))))))

(defn resolve-property-snapshot
  "Resolve a saved snapshot against the current config; :dropped? is true when any saved filter
  was omitted. States are filtered to the closed :property.state/* enum."
  [{templates :property-attribute-templates} {:keys [states filters]} lang]
  (let [resolved (keep #(resolve-property-filter lang templates %) filters)]
    {:states   (->> (or states property-model/default-search-states)
                    (filter property-model/property-states)
                    (into #{}))
     :filters  (vec resolved)
     :dropped? (not= (count filters) (count resolved))}))

(defn stored-label-fns
  "Per-tab label functions and attribute orders for the saved-search dropdown rows: each tab's
  search-mode val->str-fn over the stored lookup labels, wrapped with the shared
  saved-description formatting. Property has no search mode and formats itself in
  saved-search->row. Gated attributes default to hidden unless their flags are enabled by the supplied predicate or set."
  ([search-modes labels tr lang]
   (stored-label-fns search-modes labels tr lang #{}))
  ([search-modes labels tr lang feature-enabled?]
   (let [labels (p/update-if-contains labels :municipality/number
                                      #(mapv (fn [[number names]]
                                               [number (i18n-model/get-text-from-entities-or-map names lang)])
                                             %))]
     (into {}
           (map (fn [[tab {:keys [val->str-fn] :as search-mode}]]
                  [tab {:val->str-fn     (saved-val->str-fn (partial val->str-fn {:filter-values labels} tr) tr)
                        :attribute-order (description-attribute-order search-mode feature-enabled?)}]))
           search-modes))))

(defn saved-search->row
  "Build a dropdown row from saved data: standard-tab values labeled with the tab's stored
  label functions, Property attributes as name: label segments, without loading config."
  [domain-groups tab-labels filter-entity {:keys [label-fns] :as format-opts}]
  (let [entity-tab                            (subtype->entity-tab (:filter/subtype filter-entity))
        domain-group-ids                      (some-> filter-entity (entities/get-id :filter/domain-groups))
        {:keys [val->str-fn attribute-order]} (get label-fns entity-tab)]
    {:id           (:db/id filter-entity)
     :filter       filter-entity
     :name         (:filter/name filter-entity)
     :preview-text (preview-text
                     {:domain-group-names (->> domain-groups
                                               (filter #(contains? domain-group-ids (:db/id %)))
                                               (map :group/name))
                      :search-term        (:filter/search-term filter-entity)
                      :tab-label          (get tab-labels entity-tab)
                      :filter-value-names (cond
                                            (= :property entity-tab)
                                            (keep #(property-filter-value-name format-opts %)
                                                  (:filters (:filter/property-search filter-entity)))
                                            val->str-fn
                                            (filter-value-names
                                              val->str-fn
                                              attribute-order
                                              (filter-util/filter-entity->search-values-map filter-entity)))})}))


;;
;; Subscriptions
;;


(rf/reg-sub ::saved-searches
  :<- [::entities/entities {:entity/type :entity.type/filter}]
  (fn [filters _]
    (->> filters
         (filter (comp supported-subtypes :filter/subtype))
         (sort-by #(some-> (:filter/name %) str/lower-case))
         vec)))

(rf/reg-sub ::save-dialog-open?
  (fn [db _]
    (boolean (get-in db [::state :save-dialog :open?]))))

(rf/reg-sub ::save-dialog-name
  (fn [db _]
    (get-in db [::state :save-dialog :name])))

(rf/reg-sub ::labels
  ;; The stored lookup labels of the saved values, fetched by ::fetch-filter-labels.
  (fn [db _]
    (get-in db [::state :labels])))

(rf/reg-sub ::saving?
  (fn [db _]
    (boolean (get-in db [::state :saving?]))))


;;
;; Events
;;


(rf/reg-event-fx ::fetch-unified-filters
  ;; Save/remove flows update the entity db via apply-changes, so one successful fetch is enough.
  (fn [{:keys [db]} _]
    (when-not (get-in db [::state :unified-filters-loaded?])
      {:comm/command {:command    :user/unified-filters
                      :method     :get
                      :on-success [::fetch-unified-filters.success]}})))

(rf/reg-event-fx ::fetch-unified-filters.success
  ;; The GET response is an entity-exported map of complete filter entities, not outject
  ;; tx-changes, so apply it with ::override-entity-db instead of ::apply-changes: the
  ;; included entities are replaced with the complete response maps, so attributes omitted
  ;; from the response are removed. Other cached entities are kept; a late response is ignored
  ;; so it cannot overwrite entities updated through apply-changes meanwhile.
  (fn [{:keys [db]} [_ status response]]
    (when-not (get-in db [::state :unified-filters-loaded?])
      {:db       (assoc-in db [::state :unified-filters-loaded?] true)
       :dispatch [::entities/override-entity-db status response]})))

(rf/reg-event-fx ::fetch-filter-labels
  ;; Labels are fetched separately so save/remove tx updates stay authoritative for the
  ;; entities; a versioned request rejects an older response arriving after a newer one.
  (fn [{:keys [db]} _]
    (let [version (inc (get-in db [::state :labels-version] 0))]
      {:db           (assoc-in db [::state :labels-version] version)
       :comm/command {:command    :user/unified-filter-labels
                      :method     :get
                      :on-success [::fetch-filter-labels.success version]}})))

(rf/reg-event-fx ::fetch-filter-labels.success
  (fn [{:keys [db]} [_ version _status labels]]
    (when (= version (get-in db [::state :labels-version]))
      {:db (assoc-in db [::state :labels] labels)})))

(rf/reg-event-fx ::save-unified-search
  (fn [{:keys [db]} [_ request]]
    {:comm/command {:command          :user/save-unified-filter
                    :data             request
                    :on-success       [::save-unified-search.success]
                    ;; :on-error replaces comm's default connection notification, so the
                    ;; handler below performs both notification and cleanup.
                    :on-error         [::save-unified-search.error]
                    :finally-dispatch [::save-unified-search.done]}
     :db           (assoc-in db [::state :saving?] true)}))

(rf/reg-event-fx ::save-unified-search.success
  (fn [_ [_ status response]]
    {:dispatch-n [[::entities/apply-changes status response]
                  ;; The saved values need their display labels from the backend.
                  [::fetch-filter-labels]
                  [::close-save-dialog]
                  [:notifications/add :toast :success :success :saved-search/save-success]]}))

(rf/reg-event-fx ::save-unified-search.error
  (fn [{:keys [db]} _]
    {:db       (p/dissoc-in db [::state :saving?])
     :dispatch [:notifications/add :toast :error
                :error/connection-error-title
                :error/connection-error-description]}))

(rf/reg-event-db ::save-unified-search.done
  #(p/dissoc-in % [::state :saving?]))

(rf/reg-event-fx ::remove-saved-search
  (fn [_ [_ filter-id]]
    {:comm/command {:command    :user/remove-unified-filter
                    :data       {:id filter-id}
                    :on-success [::remove-saved-search.success]}}))

(rf/reg-event-fx ::remove-saved-search.success
  (fn [_ [_ status response]]
    {:dispatch-n [[::entities/apply-changes status response]
                  [:notifications/add :toast :success :success :saved-search/delete-success]]}))


;;
;; Save dialog state
;;

(rf/reg-event-db ::open-save-dialog
  (fn [db _]
    (assoc-in db [::state :save-dialog] {:open? true :name ""})))

(rf/reg-event-db ::close-save-dialog
  (fn [db _]
    (p/dissoc-in db [::state :save-dialog])))

(rf/reg-event-db ::set-save-dialog-name
  (fn [db [_ name]]
    (assoc-in db [::state :save-dialog :name] name)))
